import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDownIcon, DownloadIcon } from "@radix-ui/react-icons";
import { MenuProps, components } from "react-select";
import "./index.css";
import {
  AccountingSystemForCalculatingFinancialStatistics as Basis,
  ApiError,
  ExportsService,
} from "client/openapi";
import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";
import { saveAs } from "file-saver";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { Select, selectComponents } from "components/Select";
import { DateRangePicker } from "rsuite";
import { GroupButton, InfoTip } from "../Insights/controls";
import moment from "moment";
import {
  CompareChoice,
  CompareTo,
  compareChoices,
  Period,
  PresetPeriod,
  periodLabel,
  rangeLabel,
  DAY,
  Measure,
  Range,
  changeText,
  format,
} from "../Insights/data";

/** Insights' comparison on the summary bar. */
export type SummaryCompare = {
  to: CompareTo;
  onTo: (to: CompareTo) => void;
  // The compared dates, picked freely in Custom.
  range: Range;
  onRange: (range: Range) => void;
  // The compared dates' revenue and cost (cents) and hours, once loaded.
  totals: { revenue: number; cost: number; hours: number } | null;
};

const asDate = (day: string) => moment(day, DAY).toDate();
const asRange = (value: [Date, Date]): Range => [
  moment(value[0]).format(DAY),
  moment(value[1]).format(DAY),
];
// The one label style in the top section: small, bold and gray.
const LABEL = { fontSize: "12px", fontWeight: 700, color: "#6b7280" } as const;

/** A label and its control on one line, at the pills' height. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  // Wraps on a phone: the label above, the controls below.
  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
      <span style={LABEL}>{label}</span>
      {children}
    </div>
  );
}

/**
 * A pill showing a range ("This month · Oct 1 – 6, 2026") that opens the
 * calendar, with presets down the left when given. A preset clicked is that
 * preset (onPreset); only the calendar's OK gives custom dates (onPick). The
 * picker's own input sits hidden under the pill, out of the tab order, so the
 * calendar opens below it.
 */
function RangeButton({
  id,
  label,
  text,
  range,
  presets = [],
  onPick,
  onPreset,
  onOpen,
}: {
  id: string;
  label: string;
  text: string;
  range: Range;
  presets?: { value: string; label: string; range: Range }[];
  onPick: (days: Range) => void;
  onPreset?: (value: string) => void;
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const pill = useRef<HTMLButtonElement>(null);
  // A press on the pill while open closes it (outside the popup), and the
  // click that follows must not open it again. Recorded on pointerdown, which
  // comes before the popup's own close on mousedown.
  const wasOpen = useRef(false);
  const value = useMemo(
    () => [asDate(range[0]), asDate(range[1])] as [Date, Date],
    [range[0], range[1]] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const ranges = useMemo(
    () =>
      presets.map((p) => ({
        label: p.label,
        value: [asDate(p.range[0]), asDate(p.range[1])] as [Date, Date],
        placement: "left" as const,
      })),
    [presets]
  );
  const narrow =
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(max-width: 640px)").matches;
  // Focus goes back to the pill only from inside the popup (a preset, OK,
  // Escape), and without scrolling; a click elsewhere keeps its own focus.
  const close = () => {
    const popup = document.querySelector(".rs-picker-popup");
    const fromPopup = !!popup?.contains(document.activeElement);
    setOpen(false);
    if (fromPopup) pill.current?.focus({ preventScroll: true });
  };
  return (
    <div className="relative inline-block max-w-full">
      <button
        ref={pill}
        type="button"
        id={id}
        aria-label={label}
        aria-expanded={open}
        onPointerDown={() => {
          wasOpen.current = open;
        }}
        onClick={() => {
          if (wasOpen.current) {
            wasOpen.current = false;
            return;
          }
          onOpen?.();
          setOpen(true);
        }}
        className="inline-flex max-w-full items-center gap-2 h-[30px] px-4 rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100 whitespace-nowrap"
      >
        <span className="truncate">{text}</span>
        <ChevronDownIcon className="text-gray-500" />
      </button>
      <div {...{ inert: "" }}>
        <DateRangePicker
          open={open}
          onClose={close}
          onEntered={() =>
            (
              document.querySelector(
                ".rs-picker-popup button, .rs-picker-popup [tabindex='0']"
              ) as HTMLElement | null
            )?.focus()
          }
          value={value}
          ranges={ranges}
          onShortcutClick={(shortcut) => {
            const preset = presets.find((p) => p.label === shortcut.label);
            if (preset && onPreset) onPreset(preset.value);
            else if (preset) onPick(preset.range);
            close();
          }}
          onOk={(picked) => {
            // OK with the dates unchanged only closes: a preset stays a preset.
            const days = asRange(picked as [Date, Date]);
            if (days[0] !== range[0] || days[1] !== range[1]) onPick(days);
            close();
          }}
          showOneCalendar={narrow}
          preventOverflow
          format="MMM d, yyyy"
          character=" – "
          cleanable={false}
          editable={false}
          size="sm"
          placement="bottomStart"
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            pointerEvents: "none",
          }}
          tabIndex={-1}
          aria-hidden
        />
      </div>
    </div>
  );
}

/** The period button: a preset ("This month · Oct 1 – 6, 2026") or custom dates. */
function PeriodPicker({
  period,
  range,
  presets,
  onPeriod,
  onOpen,
}: {
  period: Period;
  range: Range;
  presets: { value: PresetPeriod; label: string; range: Range }[];
  onPeriod: (period: Period, custom?: Range) => void;
  onOpen?: () => void;
}) {
  return (
    <RangeButton
      id="period"
      label="Period"
      text={`${periodLabel(period)} · ${rangeLabel(range)}`}
      range={range}
      presets={presets}
      onPreset={(value) => onPeriod(value as PresetPeriod)}
      onPick={(days) => onPeriod("custom", days)}
      onOpen={onOpen}
    />
  );
}

/** A row of one-click choices, in the pills Insights uses for Day, Week, Month. */
function Choices<T extends string>({
  label,
  info,
  options,
  value,
  onChange,
}: {
  label: string;
  info?: ReactNode;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span style={LABEL}>{label}</span>
      {info}
      <div className="flex gap-2" role="group" aria-label={label}>
        {options.map((o) => (
          <GroupButton
            key={o.value}
            active={value === o.value}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </GroupButton>
        ))}
      </div>
    </div>
  );
}

const BASIS_HELP =
  "Cash basis accounting counts money when it is actually paid. Accrual basis accounting counts money when the work happens. Transactions shows cash basis accounting, and Meetings shows accrual basis accounting.";

// The Period pill's look on the app's Select: 30px, round, a gray border
// that stays gray, gray on hover, no ring after a click, and the browser's
// focus ring on keyboard focus. The menu below it is as wide as its longest
// choice and stays on the screen (FitMenu). On a phone the pill takes a line
// of its own and the menu its width.
const PHONE = "@media (max-width: 640px)";
// The browser's own focus ring, as the Period pill shows on keyboard focus.
const FOCUS_RING = ["auto 1px", "-webkit-focus-ring-color auto 1px"];
const PILL = {
  // As the app's Select: an open menu sits above everything.
  container: (base: any, state: any) => ({
    ...base,
    position: "relative",
    maxWidth: "100%",
    fontSize: "14px",
    zIndex: state.menuIsOpen ? 100000 : base.zIndex,
    [PHONE]: { width: "100%" },
  }),
  control: (base: any) => ({
    ...base,
    minHeight: 30,
    padding: "0 12px 0 16px",
    borderRadius: 9999,
    borderColor: "#d1d5db",
    boxShadow: "none",
    transition: "none",
    fontWeight: 500,
    color: "#374151",
    "&:hover": { borderColor: "#d1d5db", backgroundColor: "#f3f4f6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: 0 }),
  singleValue: (base: any) => ({ ...base, margin: 0 }),
  indicatorsContainer: (base: any) => ({ ...base, paddingLeft: 8 }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: 0,
    "&:hover": { color: "#6b7280", backgroundColor: "transparent" },
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 100000,
    width: "max-content",
    minWidth: "100%",
    [PHONE]: { width: "100%" },
  }),
};

const MARGIN = 16;

/**
 * The menu, kept on the screen with MARGIN to spare: under the pill's left
 * edge when it fits, else under its right edge, else at the margin and no
 * wider than the screen, its choices wrapping.
 */
function FitMenu({ children, ...props }: MenuProps<CompareChoice, false>) {
  const probe = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const menu = probe.current?.parentElement;
    const pill = menu?.parentElement;
    if (!menu || !pill) return;
    const fit = () => {
      menu.style.width = "";
      menu.style.left = "";
      const right = document.documentElement.clientWidth - MARGIN;
      const box = menu.getBoundingClientRect();
      if (box.right <= right) return;
      const at = pill.getBoundingClientRect();
      const width = Math.min(box.width, right - MARGIN);
      const left = Math.max(MARGIN, Math.min(at.right, right) - width);
      menu.style.width = `${width}px`;
      menu.style.left = `${left - at.left}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);
  return (
    <components.Menu {...props}>
      <span ref={probe} hidden />
      {children}
    </components.Menu>
  );
}

/**
 * Compare to: the period's choices (compareChoices) in the app's Select,
 * closed like the Period pill with the choice's name; the menu, like the
 * chart's legend, gives its dates. A choice the period folds into another
 * (Same quarter last year once the period is a year) shows as that one.
 */
function CompareSelect({
  period,
  range,
  compare,
}: {
  period: Period;
  range: Range;
  compare: SummaryCompare;
}) {
  const choices = compareChoices(period, range);
  const chosen =
    choices.find((c) => c.value === compare.to) ??
    choices.find(
      (c) =>
        c.range?.[0] === compare.range[0] && c.range?.[1] === compare.range[1]
    );
  // The focus ring for keyboard focus only, as the Period pill's: the
  // Select's input counts as focus-visible after a click too.
  const [keys, setKeys] = useState(false);
  useEffect(() => {
    const key = () => setKeys(true);
    const point = () => setKeys(false);
    document.addEventListener("keydown", key, true);
    document.addEventListener("pointerdown", point, true);
    return () => {
      document.removeEventListener("keydown", key, true);
      document.removeEventListener("pointerdown", point, true);
    };
  }, []);
  return (
    <Select
      inputId="compare-to"
      aria-label="Compare to"
      isSearchable={false}
      isClearable={false}
      options={choices}
      value={chosen}
      onChange={(choice: CompareChoice) => compare.onTo(choice.value)}
      formatOptionLabel={(
        choice: CompareChoice,
        { context }: { context: "menu" | "value" }
      ) => (context === "value" ? choice.name : choice.label)}
      components={{
        ...selectComponents,
        IndicatorSeparator: null,
        Menu: FitMenu,
      }}
      styles={{
        ...PILL,
        control: (base: any, state: any) => ({
          ...PILL.control(base),
          ...(state.isFocused && keys ? { outline: FOCUS_RING } : {}),
        }),
      }}
    />
  );
}

function Statistics({
  orgId,
  period,
  range,
  presets,
  onPeriod,
  onPeriodOpen,
  rangeStart,
  rangeUntil,
  revenue,
  cost,
  hours,
  compare,
  dim,
  basis,
  onBasis,
}: {
  orgId: number;
  period: Period;
  range: Range;
  presets: { value: PresetPeriod; label: string; range: Range }[];
  onPeriod: (period: Period, custom?: Range) => void;
  // Called as the period picker opens, to count presets from today.
  onPeriodOpen?: () => void;
  // The picked days as moments, cut in the organization's time zone.
  rangeStart: string;
  rangeUntil: string;
  revenue: number;
  cost: number;
  // Insights only: the fourth column, and compare mode.
  // Null while it loads or if it can't (shown as a dash).
  hours?: number | null;
  compare?: SummaryCompare;
  // Dimmed while Insights reloads its numbers.
  dim?: boolean;
  basis: Basis;
  onBasis: (basis: Basis) => void;
}) {
  const against = compare && compare.to !== "none" ? compare.totals : null;
  // Each change is the value shown less the compared value shown, on its own
  // line under the compared value.
  const change = (measure: Measure, now: number | null, then?: number) =>
    against && then !== undefined ? (
      <p className="StatisticsChange">
        <span className="block">vs {format(measure, then)}</span>
        {now !== null && (
          <span className="block">{changeText(measure, now, then)}</span>
        )}
      </p>
    ) : null;
  const [status, setStatus] = useState<PageStatus>();
  const [error, setError] = useState<APIResponse>();
  const [dontSpamExport, setDontSpamExport] = useState<boolean>(false);

  function ExportTransactions() {
    ExportsService.exportOrganizationTransactions({
      orgId: orgId,
      start: rangeStart,
      until: rangeUntil,
    })
      .then((data) => {
        try {
          const blob = new Blob([data], { type: "text/csv" });
          const fileName = "export.csv";
          saveAs(blob, fileName);
          Notifications.success(
            `Transactions have been saved to your computer!`
          );
        } catch (error) {
          console.error(error);
          Notifications.error("Unable to save file.");
        }
        setDontSpamExport(false);
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError({
          message: `Unable to export transactions`,
        });
        console.error(`Error (#${e.status}): ${e.message}`);
        setDontSpamExport(false);
      });
  }

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  const exportButton = (
    <Button
      onClick={() => {
        setDontSpamExport(true);
        ExportTransactions();
      }}
      disabled={dontSpamExport}
      color={dontSpamExport ? ButtonColor.GRAY : ButtonColor.DARK_BLUE}
      size={ButtonSize.SMALL}
      extraClasses="!h-[30px] !w-[30px] !p-0 !rounded-full flex items-center justify-center"
      aria-label="Export data"
      title="Export data"
    >
      <DownloadIcon aria-hidden />
    </Button>
  );
  return (
    <div className="StatisticsContainer w-full border-b-2 px-4 py-3 space-y-3">
      {/* Row 1: what the page shows, then the export icon at the right. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <Field label="Period">
          <PeriodPicker
            period={period}
            range={range}
            presets={presets}
            onPeriod={onPeriod}
            onOpen={onPeriodOpen}
          />
        </Field>
        {compare && (
          <Field label="Compare to">
            <CompareSelect period={period} range={range} compare={compare} />
            {compare.to === "custom" && (
              <RangeButton
                id="compared-with"
                label="Compared with"
                text={rangeLabel(compare.range)}
                range={compare.range}
                onPick={compare.onRange}
              />
            )}
          </Field>
        )}
        <Choices
          label="Accounting method"
          info={
            <InfoTip label="About accounting methods">{BASIS_HELP}</InfoTip>
          }
          options={[
            { value: Basis.TRANSACTIONS, label: "Transactions" },
            { value: Basis.MEETINGS, label: "Meetings" },
          ]}
          value={basis}
          onChange={onBasis}
        />
        <div className="ml-auto">{exportButton}</div>
      </div>
      <div className="border-t border-gray-200" />
      {/* Row 2: the numbers, in equal columns. */}
      <div
        className={`grid grid-cols-2 gap-x-6 gap-y-2 ${
          hours !== undefined ? "lg:grid-cols-4" : "lg:grid-cols-3"
        } ${dim ? "opacity-60" : ""}`}
      >
        <div className="StatisticsBox !mt-0">
          Revenue
          <div style={{ color: "#00C56B" }}>
            $
            {(revenue / 100).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          {change(
            "revenue",
            revenue / 100,
            against ? against.revenue / 100 : undefined
          )}
        </div>
        <div className="StatisticsBox !mt-0">
          Cost of Tutoring
          <div style={{ color: "#FFB341" }}>
            $
            {(cost / 100).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          {change("cost", cost / 100, against ? against.cost / 100 : undefined)}
        </div>
        <div className="StatisticsBox !mt-0">
          Net Income
          <div style={{ color: "#809BF7" }}>
            $
            {((revenue - cost) / 100).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          {change(
            "net",
            (revenue - cost) / 100,
            against ? (against.revenue - against.cost) / 100 : undefined
          )}
        </div>
        {hours !== undefined && (
          <div className="StatisticsBox !mt-0">
            Hours
            <div style={{ color: "#7ABFFF" }}>
              {hours === null
                ? "—"
                : `${hours.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })} hrs`}
            </div>
            {change(
              "hours",
              hours ?? null,
              against ? against.hours : undefined
            )}
          </div>
        )}
      </div>
    </div>
  );
}
export default Statistics;
